/**
 * Auth bridge for cross-origin iframe communication.
 *
 * Parent (prod) sends JWT to child (dev iframe) via postMessage.
 * Child listens and stores the token so it can authenticate against the same backend.
 */

import { getAuthToken, setAuthToken } from './auth'

const AUTH_MESSAGE_TYPE = 'dashboardos:auth'

interface AuthMessage {
  type: typeof AUTH_MESSAGE_TYPE
  token: string
}

function isAuthMessage(data: unknown): data is AuthMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as AuthMessage).type === AUTH_MESSAGE_TYPE &&
    'token' in data &&
    typeof (data as AuthMessage).token === 'string'
  )
}

/**
 * Send the current JWT to an iframe's content window.
 * Called by the parent (production) app.
 */
export function sendAuthToIframe(iframe: HTMLIFrameElement, targetOrigin: string): void {
  const token = getAuthToken()
  if (!token || !iframe.contentWindow) return

  const message: AuthMessage = { type: AUTH_MESSAGE_TYPE, token }
  iframe.contentWindow.postMessage(message, targetOrigin)
}

/**
 * Listen for JWT from a parent window.
 * Called by the child (dev) app when loaded inside an iframe.
 *
 * @param allowedOrigins - List of origins allowed to send auth tokens
 * @param onAuth - Callback fired after token is stored
 */
export function listenForParentAuth(
  allowedOrigins: string[],
  onAuth?: (token: string) => void,
): () => void {
  function handler(event: MessageEvent) {
    if (!allowedOrigins.includes(event.origin)) return
    if (!isAuthMessage(event.data)) return

    setAuthToken(event.data.token)
    onAuth?.(event.data.token)
  }

  window.addEventListener('message', handler)
  return () => window.removeEventListener('message', handler)
}

/**
 * Check if the current window is inside an iframe.
 */
export function isInsideIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin restriction means we're in an iframe
    return true
  }
}
